import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { withLogWsTicket } from '../application/liveWs';
import { openLiveConnection, type LiveConnection } from '../application/liveConnection';
import { Api } from '../application/panelApi';
import { fmtShortDate, nf } from '../lib/format';



export type ChartPoint = { date: string; d: string; sent: number; ok: number; neuroSent: number; neuroOk: number };

export type LiveStats = Partial<
  Record<'total' | 'active' | 'plan' | 'ok' | 'failed' | 'sent' | 'miss' | 'error', number>
>;

const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
const LIVE_KEYS = ['total', 'active', 'plan', 'ok', 'failed', 'sent', 'miss', 'error'] as const;

function count(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function normalizeChart(rows: unknown): ChartPoint[] {
  const list = Array.isArray(rows) ? rows : [];
  const out: ChartPoint[] = [];
  for (const row of list) {
    if (!row || typeof row !== 'object') continue;
    const r = row as { date?: unknown; sent?: unknown; ok?: unknown; neuroSent?: unknown; neuroOk?: unknown };
    const date = String(r.date ?? '');
    if (!DATE_KEY.test(date)) continue;
    out.push({
      date,
      d: fmtShortDate(date),
      sent: count(r.sent),
      ok: count(r.ok),
      neuroSent: count(r.neuroSent),
      neuroOk: count(r.neuroOk),
    });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

export function chartTotals(points: ChartPoint[]): { ok: number; sent: number } {
  return {
    ok: points.reduce((a, r) => a + r.ok, 0),
    sent: points.reduce((a, r) => a + r.sent, 0),
  };
}


export function todayRateLabel(points: ChartPoint[], now: Date = new Date()): string {
  const todayKey = now.toISOString().slice(0, 10);
  const today = points.find((r) => r.date === todayKey);
  if (!today || today.ok <= 0) return '—';
  const hours = now.getUTCHours() + now.getUTCMinutes() / 60;
  return hours > 0 ? `${nf.format(Math.round(today.ok / hours))} / час` : '—';
}

function pickLiveStats(raw: unknown): LiveStats | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Record<string, unknown>;
  const out: LiveStats = {};
  for (const key of LIVE_KEYS) {
    const v = src[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = Math.max(0, Math.floor(v));
  }
  return Object.keys(out).length ? out : null;
}




let chartCache: ChartPoint[] = [];
const chartListeners = new Set<(points: ChartPoint[]) => void>();

function publishChart(next: ChartPoint[]): void {
  chartCache = next;
  for (const listener of chartListeners) listener(next);
}

declare global {
  interface Window {

    __bcSetChartData?: (rows: unknown) => void;
  }
}

if (typeof window !== 'undefined') {
  window.__bcSetChartData = (rows: unknown) => {
    publishChart(normalizeChart(rows));
  };
}

type UseTasksChartOptions = {

  live?: boolean;

  onStats?: (stats: LiveStats) => void;
};

export function useTasksChart(opts: UseTasksChartOptions = {}): { points: ChartPoint[]; reload: () => Promise<void> } {
  const { live = true, onStats } = opts;
  const [points, setPoints] = useState<ChartPoint[]>(chartCache);
  const onStatsRef = useRef(onStats);
  onStatsRef.current = onStats;

  useEffect(() => {
    const listener = (next: ChartPoint[]) => setPoints(next);
    chartListeners.add(listener);
    return () => {
      chartListeners.delete(listener);
    };
  }, []);

  const apply = useCallback((rows: unknown) => {
    publishChart(normalizeChart(rows));
  }, []);

  const reload = useCallback(async () => {
    try {
      const data = await Api.tasks.chart();
      apply(data?.chart);
    } catch {

    }
  }, [apply]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!live) return undefined;
    let ws: LiveConnection | null = null;
    let timer: number | undefined;
    let delay = 3000;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      void withLogWsTicket(`${proto}//${location.host}/api/logs/ws`).then((url) => {
        if (stopped) return;
        try {
          ws = openLiveConnection(url);
        } catch {
          timer = window.setTimeout(connect, delay);
          return;
        }
        bind(ws);
      });
    };

    const bind = (next: LiveConnection) => {
      ws = next;
      ws.onopen = () => {
        delay = 3000;
      };
      ws.onmessage = (ev) => {
        type LiveMsg = { type?: string; chart?: unknown; stats?: unknown };
        let msg: LiveMsg | null;
        try {
          msg = JSON.parse(String(ev.data)) as LiveMsg | null;
        } catch {
          return;
        }
        if (!msg || msg.type !== 'tasks_live') return;
        if (Array.isArray(msg.chart)) apply(msg.chart);
        const stats = pickLiveStats(msg.stats);
        if (stats) onStatsRef.current?.(stats);
      };
      ws.onclose = () => {
        ws = null;
        if (stopped) return;
        timer = window.setTimeout(connect, delay);
        delay = Math.min(delay * 2, 15000);
      };
      ws.onerror = () => {
        try {
          ws?.close();
        } catch {

        }
      };
    };

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      if (ws) {
        ws.onclose = null;
        try {
          ws.close();
        } catch {

        }
      }
    };
  }, [live, apply]);

  return { points, reload };
}



function xOf(i: number, n: number, L: number, R: number, W: number): number {
  if (n <= 1) return (L + W - R) / 2;
  return L + (i * (W - L - R)) / (n - 1);
}

function indexAt(px: number, n: number, L: number, R: number, W: number): number {
  if (n <= 1) return 0;
  const span = (W - L - R) / (n - 1);
  if (!Number.isFinite(span) || span <= 0) return 0;
  const i = Math.round((px - L) / span);
  return Math.max(0, Math.min(n - 1, i));
}

const H = 158;
const L = 42;
const R = 14;
const T = 10;
const B = 24;
const COLOR_SENT = '#2A7BD0';
const COLOR_OK = '#7A5CBF';

const fmtAxis = (v: number): string =>
  v >= 1000 ? `${(v / 1000).toFixed(1).replace('.0', '')}K` : String(v);



type TasksChartProps = {
  points: ChartPoint[];
  showOk: boolean;
  showSent: boolean;
};

export function TasksChart({ points, showOk, showSent }: TasksChartProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  const [hover, setHover] = useState<{ i: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const measure = () => setWidth(Math.max(520, box.clientWidth || 900));
    measure();
    let timer: number | undefined;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 150);
    };
    window.addEventListener('resize', onResize);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResize);
      ro.observe(box);
    }
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, []);

  const hasData = points.some((p) => p.sent > 0 || p.ok > 0);
  const n = points.length;
  const W = width;

  if (!n || !hasData) {
    return (
      <div id="chart" ref={boxRef}>
        <div className="empty" style={{ padding: 24 }}>
          Нет данных за выбранный период
        </div>
      </div>
    );
  }

  const peak = Math.max(0, ...points.map((p) => Math.max(p.sent, p.ok)));
  const raw = peak > 0 ? peak * 1.1 : 10;
  const step = Math.pow(10, Math.floor(Math.log10(raw / 3)));
  const nice = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((k) => k * step).find((k) => raw / 3 <= k) || step * 10;
  const max = nice * 3 || 1;
  const x = (i: number) => xOf(i, n, L, R, W);
  const y = (v: number) => T + (1 - v / max) * (H - T - B);
  const line = (key: 'sent' | 'ok') => points.map((p, i) => `${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' L');

  const onMove = (ev: ReactMouseEvent<HTMLDivElement>) => {
    const svg = boxRef.current?.querySelector('svg');
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    const px = r.width > 0 ? (ev.clientX - r.left) * (W / r.width) : 0;
    const i = indexAt(px, n, L, R, W);
    const left = (x(i) / W) * r.width;
    setHover({ i, left: Math.min(Math.max(left, 70), Math.max(70, r.width - 70)) });
  };

  const hp = hover ? points[hover.i] : undefined;
  const hx = hover && hp ? x(hover.i) : 0;

  const series = (key: 'sent' | 'ok', color: string, on: boolean) =>
    on ? (
      <g key={key}>
        <path d={`M${line(key)}`} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(n - 1).toFixed(1)} cy={y(points[n - 1][key]).toFixed(1)} r="3" fill={color} />
      </g>
    ) : null;

  return (
    <div id="chart" ref={boxRef} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="Клики за 14 дней">
        {[0, 1, 2, 3]
          .map((f) => nice * f)
          .map((v) => (
            <g key={`g${v}`}>
              <line x1={L} x2={W - R} y1={y(v).toFixed(1)} y2={y(v).toFixed(1)} stroke="var(--border)" strokeWidth="1" />
              <text x={L - 8} y={(y(v) + 3.5).toFixed(1)} textAnchor="end" fontSize="10" fill="var(--text-3)">
                {fmtAxis(v)}
              </text>
            </g>
          ))}
        {points.map((p, i) =>
          i % 4 === 0 || i === n - 1 ? (
            <text key={`x${p.date}`} x={x(i).toFixed(1)} y={H - 8} textAnchor="middle" fontSize="10" fill="var(--text-3)">
              {p.d}
            </text>
          ) : null,
        )}
        {series('sent', COLOR_SENT, showSent)}
        {series('ok', COLOR_OK, showOk)}
        <line
          id="chartRule"
          x1={hx}
          x2={hx}
          y1={T}
          y2={H - B}
          stroke="var(--border-strong)"
          strokeWidth="1"
          opacity={hover ? 1 : 0}
        />
      </svg>
      <div className="chart-tip" id="chartTip" hidden={!hover || !hp} style={hover ? { left: `${hover.left}px` } : undefined}>
        {hp && (
          <>
            <b>{hp.d}</b>
            {showOk && (
              <span>
                <i style={{ background: COLOR_OK }} />
                Успешные <b>{nf.format(hp.ok)}</b>
              </span>
            )}
            {showSent && (
              <span>
                <i style={{ background: COLOR_SENT }} />
                Отправлено <b>{nf.format(hp.sent)}</b>
              </span>
            )}
            {hp.sent > 0 && <span className="tip-rate">Доля успеха {((hp.ok / hp.sent) * 100).toFixed(1)}%</span>}
          </>
        )}
      </div>
    </div>
  );
}



export function Spark({ points }: { points: ChartPoint[] }) {
  const pts = points.slice(-12).map((r) => r.ok);
  const has = pts.length > 0 && pts.some((v) => v > 0);
  let inner = null;
  if (has) {
    const max = Math.max(...pts, 1);
    const denom = Math.max(1, pts.length - 1);
    const d = pts.map((v, i) => `${((i / denom) * 200).toFixed(1)},${(28 - (v / max) * 24).toFixed(1)}`).join(' L');
    const lastY = (28 - (pts[pts.length - 1] / max) * 24).toFixed(1);
    inner = (
      <>
        <path d={`M${d} L200,30 L0,30 Z`} fill="var(--accent-soft)" stroke="none" />
        <path d={`M${d}`} fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        <circle cx={pts.length <= 1 ? 100 : 199} cy={lastY} r="2.4" fill="var(--accent)" />
      </>
    );
  }
  return (
    <svg className="spark" id="spark" viewBox="0 0 200 30" preserveAspectRatio="none" aria-hidden="true">
      {inner}
    </svg>
  );
}
